<script setup lang="ts">
import { computed, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type {
	GetDetailProductResponse,
	ProductList,
} from '@/services/pcsServiceTypes'
import { Switch } from '@/components/ui/switch'
import ProductNoOrderAvail from './_ProductNoOrderAvail.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import Spinner from '@/pages/search/components/_Spinner.vue'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDisplayPrice, formatDateToYYYYMMDD } from '@/lib/utils'
import { isLogin } from '@/store/userStore'
import { getMcrRootUrl } from '@/lib/siteBaseUrl'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const { t } = useI18n()

const props = defineProps({
	product: {
		type: Object as () => GetDetailProductResponse | null,
		default: null,
	},
	isShowMore: Boolean,
	isLoading: Boolean,
	buyforyouWarehouses: {
		type: Array as () => string[],
		default: () => [],
	},
})

const canShowOrderButton = (warehouseId: string) => {
	return (
		props.buyforyouWarehouses && props.buyforyouWarehouses.includes(warehouseId)
	)
}

const emit = defineEmits(['loadMoreData'])

const isChecked = ref(false)
const displayedCurrency = ref('HK$')
const showRawTitle = ref(false)

const isUserVerified: ComputedRef<Boolean> = computed(
	() => window.BUYANDSHIP.settings.user_onboarded,
)
const buttonText = computed(() => {
	return props.isLoading ? 'Please Wait' : 'Show More'
})
const showSpinner = computed(() => props.isLoading)
const buttonDisabled = computed(() => props.isLoading)
const showButton = computed(() => props.isLoading || props.isShowMore)
const displayedProducts = computed(() => {
	return props.product
		? isChecked.value
			? props.product.products.filter(item => item.availability === 'A')
			: props.product.products
		: []
})

const toggleTitle = () => {
	showRawTitle.value = !showRawTitle.value
}

const handleSwitchChange = () => {
	isChecked.value = !isChecked.value
}

const loadMoreData = () => {
	if (!props.isLoading) {
		emit('loadMoreData')
	}
}

const getUrlQueryTransform = (queryObj: any) => {
	const urlParam: any[] = []

	Object.keys(queryObj).forEach(key => {
		urlParam.push(`${key}=${encodeURIComponent(queryObj[key])}`)
	})

	return urlParam
}

const postServiceAction = (props: ProductList) => {
	if (!isLogin.value) {
		window.BsHeaderVm.showPanel('login')
		return
	}
	const query = getUrlQueryTransform({
		default: 'true',
		itemName: props.title,
		price: props.price,
		link: props.link,
		warehouseId: props.warehouse_id,
	})
	const urlPrefix = isUserVerified.value
		? `/buyforyou/new?${query.join('&')}`
		: `/account/productsharing/posts/?create_profile=true&${query.join('&')}`

	const memberCenterUrl = `${getMcrRootUrl()}${urlPrefix}`
	window.open(memberCenterUrl, '_blank')
}
</script>

<template>
	<Tabs v-if="product" default-value="prices">
		<TabsList class="bs-container bg-white">
			<div class="col-span-12">
				<div class="flex">
					<TabsTrigger value="prices" class="w-full mdp:w-auto">
						{{ t('pcs_product_prices_tab') }}
					</TabsTrigger>
					<TabsTrigger value="product-details" class="w-full mdp:w-auto">
						{{ t('pcs_product_details_tab') }}
					</TabsTrigger>
				</div>
			</div>
		</TabsList>

		<div class="bg-n-50">
			<div class="bs-container">
				<div class="col-span-12">
					<TabsContent value="prices" class="bg-n-50">
						<div class="py-distant-acquaintances">
							<div
								class="flex flex-col justify-between gap-24px mdp:flex-row-reverse"
							>
								<!-- Translation Tool -->
								<div
									class="typography-b3-reg mt-good-friends flex w-full justify-between gap-16px bg-white px-16px py-8px text-center mdp:mt-0 mdp:w-auto"
								>
									<span class="font-normal">
										{{ t('pcs_auto_translated_message') }}
									</span>
									<span
										class="cursor-pointer select-none font-medium text-o-600 underline hover:text-o-500 hover:no-underline"
										@click="toggleTitle"
									>
										{{
											showRawTitle
												? t('pcs_hide_original_language')
												: t('pcs_show_original_language')
										}}
									</span>
								</div>

								<!-- Switch -->
								<div class="flex items-center gap-16px">
									<Switch :checked="isChecked" @click="handleSwitchChange" />
									<span class="typography-s2-reg">
										{{ t('pcs_show_order_avail') }}
									</span>
								</div>
							</div>

							<div v-if="displayedProducts.length">
								<!-- List -->
								<div class="flex flex-col gap-16px py-casual-friends">
									<div
										v-for="item in product.products"
										:key="item.title"
										class="flex flex-col gap-12px rounded-lg border border-n-100 bg-white px-16px py-12px hover:shadow-[0px_4px_10px_0px_rgba(0,0,0,0.1)] mdl:flex-row mdl:justify-between"
										v-show="!isChecked || item.availability === 'A'"
									>
										<div class="flex flex-col justify-center gap-8px mdl:w-1/2">
											<!-- External Link -->
											<div class="flex items-center gap-8px">
												<a
													:href="item.link"
													class="typography-s1-emp font-medium text-o-600 underline"
												>
													{{ item.source }}
												</a>
												<BaseIcon
													name="new-tab"
													class="h-16px w-16px text-n-950"
												/>
											</div>
											<!-- Title -->
											<div class="typography-s1-reg line-clamp-1">
												{{ showRawTitle ? item.raw_title : item.title }}
											</div>
										</div>

										<!-- Card -->
										<div
											class="flex flex-col gap-8px rounded-lg bg-wg-50 px-16px py-12px mdp:flex-row mdp:justify-between mdl:w-1/2"
										>
											<div class="flex flex-col gap-8px mdp:gap-12px">
												<div class="flex items-center gap-8px">
													<span class="typography-h5-str">
														{{ displayedCurrency }}
														{{ formatDisplayPrice(item.display_ccy_price) }}
													</span>
													<span class="typography-c1-reg text-n-500">
														(
														{{ item.region }}
														{{ formatDisplayPrice(item.price) }}
														)
													</span>
												</div>
												<div class="typography-c2-reg text-n-500">
													{{ t('pcs_product_last_updated') }}
													{{ formatDateToYYYYMMDD(item.updated_at) }}
												</div>
											</div>

											<div v-if="canShowOrderButton(item.warehouse_id)">
												<BaseButton
													variant="pcs-primary"
													size="medium"
													class="hidden mdp:block"
													@click="postServiceAction(item)"
												>
													{{ t('pcs_product_place_order') }}
												</BaseButton>
											</div>
										</div>

										<!-- Mobile Button -->
										<div
											v-if="item.availability === 'A'"
											class="block mdp:hidden"
										>
											<BaseButton
												variant="pcs-primary"
												class="w-full"
												@click="postServiceAction(item)"
											>
												{{ t('pcs_product_place_order') }}
											</BaseButton>
										</div>
									</div>
								</div>

								<!-- Show more button -->
								<div
									v-if="showButton"
									class="item-center flex justify-center mdp:mt-60px"
								>
									<BaseButton
										@click="loadMoreData"
										variant="secondary"
										:disabled="buttonDisabled"
									>
										<template v-if="showSpinner" #leadingIcon>
											<Spinner />
										</template>
										{{ buttonText }}
									</BaseButton>
								</div>
							</div>

							<ProductNoOrderAvail v-else />
						</div>
					</TabsContent>

					<TabsContent value="product-details">
						<div class="bg-n-50 py-distant-acquaintances">
							<div class="rounded-lg bg-white p-24px mdp:p-32px">
								<h2 class="typography-h4-str mb-20px">
									{{ t('pcs_product_detail_header') }}
								</h2>
								<hr class="text-n-100" />

								<!-- name & description -->
								<div class="flex flex-col gap-good-friends py-casual-friends">
									<div class="flex flex-col gap-8px mdp:flex-row">
										<h3 class="typography-s1-reg text-n-500 mdp:min-w-[240px]">
											{{ t('pcs_product_detail_name') }}
										</h3>
										<h3 class="typography-b2-reg">
											{{ product.detail_info.title }}
										</h3>
									</div>
									<div class="flex flex-col gap-8px mdp:flex-row">
										<h3 class="typography-s1-reg text-n-500 mdp:min-w-[240px]">
											{{ t('pcs_product_detail_description') }}
										</h3>
										<h3 class="typography-b2-reg break-all">
											{{ product.detail_info.description }}
										</h3>
									</div>
								</div>

								<hr class="text-n-100" />

								<!-- brand & category -->
								<div class="flex flex-col gap-good-friends py-casual-friends">
									<div class="flex flex-col gap-8px mdp:flex-row">
										<h3 class="typography-s1-reg text-n-500 mdp:min-w-[240px]">
											{{ t('pcs_product_detail_brand') }}
										</h3>
										<h3 class="typography-b2-reg">
											{{ product.detail_info.brand_name }}
										</h3>
									</div>
									<div class="flex flex-col gap-8px mdp:flex-row">
										<h3 class="typography-s1-reg text-n-500 mdp:min-w-[240px]">
											{{ t('pcs_product_detail_category') }}
										</h3>
										<h3 class="typography-b2-reg">
											{{ product.detail_info.category }}
										</h3>
									</div>
								</div>

								<hr class="text-n-100" />

								<!-- modelList => series & color & operating system & storage & ram -->
								<div class="flex flex-col gap-good-friends py-casual-friends">
									<div
										v-for="(attribute, index) in product.detail_info.attributes"
										:key="index"
										class="flex flex-col gap-8px mdp:flex-row"
									>
										<h3 class="typography-s1-reg text-n-500 mdp:min-w-[240px]">
											{{ attribute.name }}
										</h3>
										<h3 class="typography-b2-reg">{{ attribute.value }}</h3>
									</div>
								</div>
							</div>
						</div>
					</TabsContent>
				</div>
			</div>
		</div>
	</Tabs>
</template>
